import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body } from '../op-types';

/**
 * Fox F8 — the user's alert rules.
 *
 * One flat list per user: every bell on every research page reads the SAME
 * `alerts.all` entry and filters it client-side by symbol and type. A per-symbol
 * query key would put one request behind each of the six bells on a page and
 * still not tell a bell whether a rule exists on a page it is not mounted on.
 */
export function createAlertQueries(client: StoxlyClient) {
  return createQueryKeys('alerts', {
    // ['alerts', 'all']
    all: {
      queryKey: null,
      queryFn: ({ signal }) => client.alertsFindAll({ signal }),
    },

    // ['alerts', 'dividendCalendar', symbol] — the .ics document for a symbol's
    // dividend schedule.
    //
    // The "Add to calendar" button does NOT go through here: it needs the Blob
    // and the server's attachment filename, which is `core/api/download-client`'s
    // job (the one sanctioned non-spine HTTP path). This entry is the typed
    // accessor the api-query coverage contract requires for every operation in
    // the spec, and the path any surface that wants the document as TEXT should
    // take rather than opening a second HTTP client.
    dividendCalendar: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.alertsDividendCalendar({ queries: { symbol }, signal }),
    }),
  });
}

export function createAlertMutations(client: StoxlyClient) {
  return {
    create: {
      mutationFn: (body: Body<'alertsCreate'>) => client.alertsCreate(body),
    },
    update: {
      mutationFn: ({ id, body }: { id: string; body: Body<'alertsUpdate'> }) =>
        client.alertsUpdate(body, { params: { id } }),
    },
    delete: {
      mutationFn: (id: string) => client.alertsRemove(undefined, { params: { id } }),
    },
  };
}
